import {
  AltitudeUnit,
  FuelConsumptionUnit,
  FuelQuantityUnit,
  LinearMeasureUnit,
  RateUnit,
  SpeedUnit,
  Unit,
  VolumeUnit,
  WeightUnit
} from '@domain/protocols/Unit'

import { FuelType } from '@/domain/protocols/AircraftType'

import { nanCorrection, roundToDecimals } from './number'
import { isNotSet } from './validation'

type ScalarConversionParams<T> = {
  value: number
  from?: T
  to?: T
  decimals?: number
}

type FuelQuantityConversionParams = {
  value: number
  from?: FuelQuantityUnit
  to?: FuelQuantityUnit
  fuelType?: FuelType
  decimals?: number
}

type FuelConsumptionConversionParams = {
  value: number
  from?: FuelConsumptionUnit
  to?: FuelConsumptionUnit
  fuelType?: FuelType
  decimals?: number
}

type FuelQuantityToWeightParams = {
  value: number
  from: FuelQuantityUnit
  to: WeightUnit
  fuelType?: FuelType
  decimals?: number
}

type WeightToFuelQuantityParams = {
  value: number
  from: WeightUnit
  to: FuelQuantityUnit
  fuelType?: FuelType
  decimals?: number
}

const FUEL_DENSITY_KG_PER_LITER = {
  avgas: 0.7185,
  'jet-a': 0.8028,
  ethanol: 0.7893,
  mogas: 0.745
} as const satisfies Record<FuelType, number>

const DOMAIN_UNITS = {
  altitude: 'ft' as AltitudeUnit,
  fuelConsumption: 'l/h' as FuelConsumptionUnit,
  fuelQuantity: 'l' as FuelQuantityUnit,
  linearMeasure: 'in' as LinearMeasureUnit,
  rate: 'ft/min' as RateUnit,
  speed: 'kt' as SpeedUnit,
  volume: 'l' as VolumeUnit,
  weight: 'kg' as WeightUnit
} as const

export const DEFAULT_FUEL_TYPE = 'avgas' as const

/**
 * O tipo de combustível com que calcular, com a gasolina de aviação como padrão.
 *
 * O padrão importa menos que **por que o teste é de valor falsy e não de nulo**: a aeronave pode
 * carregar o tipo de combustível como **string vazia** — a coluna chega assim para a grande maioria
 * das que não o declaram —, e `??` deixa `''` passar adiante como se fosse um tipo declarado. Foi
 * por aí que o cálculo de combustível do P&B parou de acontecer em silêncio (PLA-335), e é o mesmo
 * defeito que o cliente móvel encontrou.
 *
 * A densidade só é consultada quando a conversão atravessa volume ↔ peso: num perfil em `kg` ou `lb`
 * o padrão daqui não muda resultado nenhum. Quem precisa avisar o piloto de que a densidade foi
 * adivinhada testa o valor declarado, não o resolvido.
 */
export const resolveFuelType = (fuelType?: FuelType | null): FuelType => fuelType || DEFAULT_FUEL_TYPE

/**
 * Cada fator é **quantas unidades daquela grandeza cabem em uma unidade de domínio**
 * (`DOMAIN_UNITS`) — é assim que `convertWithFactors` o lê: divide pelo fator da unidade de origem
 * para chegar ao domínio e multiplica pelo da unidade de destino. Daí `weight.lb = 2.20462` ser libras
 * por quilograma, e não o contrário.
 *
 * A conferência de `linearMeasure` é a régua: uma polegada tem 2,54 cm, 25,4 mm e 0,0254 m, logo o
 * fator do `cm` vale 100× o do `m` e o do `mm` vale 1000× o do `m`. Um `mm` menor que o `m` é o
 * defeito, não a escala.
 *
 * O `satisfies Record<XUnit, number>` de cada grandeza é o que faz uma unidade nova sem fator **não
 * compilar**, em vez de virar `undefined` e sair como `NaN` na primeira conversão.
 *
 * Os fatores são derivados das definições, não digitados de memória: o nó vale 1,852 km/h e a milha
 * terrestre 1,609344 km, logo `mph = 1,852 / 1,609344`; o galão imperial vale 4,54609 L, logo
 * `imp-gal = 1 / 4,54609`. Todos ficam com o mesmo número de casas, e o erro de arredondamento fica
 * na mesma ordem em toda a tabela — cerca de 1e-4 % no pior caso.
 */
const FACTORS = {
  altitude: {
    ft: 1,
    m: 0.3048
  } satisfies Record<AltitudeUnit, number>,
  linearMeasure: {
    in: 1,
    cm: 2.54,
    m: 0.0254,
    mm: 25.4
  } satisfies Record<LinearMeasureUnit, number>,
  rate: {
    'ft/min': 1,
    'm/s': 0.00508
  } satisfies Record<RateUnit, number>,
  speed: {
    kt: 1,
    'km/h': 1.852,
    mph: 1.150779
  } satisfies Record<SpeedUnit, number>,
  volume: {
    l: 1,
    'imp-gal': 0.219969,
    'us-gal': 0.264172
  } satisfies Record<VolumeUnit, number>,
  weight: {
    kg: 1,
    lb: 2.20462
  } satisfies Record<WeightUnit, number>
} as const

const DISPLAY_MAPS = {
  altitude: {
    ft: 'ft',
    m: 'm'
  } satisfies Record<AltitudeUnit, string>,
  fuelConsumption: {
    'l/h': 'L/h',
    'kg/h': 'kg/h',
    'lb/h': 'lb/h',
    'imp-gal/h': 'IMP Gal/h',
    'us-gal/h': 'US Gal/h'
  } satisfies Record<FuelConsumptionUnit, string>,
  fuelQuantity: {
    l: 'L',
    kg: 'kg',
    lb: 'lb',
    'imp-gal': 'IMP Gal',
    'us-gal': 'US Gal'
  } satisfies Record<FuelQuantityUnit, string>,
  linearMeasure: {
    in: 'in',
    cm: 'cm',
    m: 'm',
    mm: 'mm'
  } satisfies Record<LinearMeasureUnit, string>,
  volume: {
    l: 'L',
    'imp-gal': 'IMP Gal',
    'us-gal': 'US Gal'
  } satisfies Record<VolumeUnit, string>,
  weight: {
    kg: 'kg',
    lb: 'lb'
  } satisfies Record<WeightUnit, string>
} as const

const VALID_WEIGHT_UNITS = new Set<WeightUnit>(['kg', 'lb'])
const VALID_VOLUME_UNITS = new Set<VolumeUnit>(['l', 'imp-gal', 'us-gal'])
const VALID_LINEAR_MEASURE_UNITS = new Set<LinearMeasureUnit>(['in', 'cm', 'm', 'mm'])
const VALID_ALTITUDE_UNITS = new Set<AltitudeUnit>(['ft', 'm'])
const VALID_SPEED_UNITS = new Set<SpeedUnit>(['kt', 'km/h', 'mph'])
const VALID_RATE_UNITS = new Set<RateUnit>(['ft/min', 'm/s'])
const VALID_FUEL_QUANTITY_UNITS = new Set<FuelQuantityUnit>(['l', 'kg', 'lb', 'imp-gal', 'us-gal'])
const VALID_FUEL_CONSUMPTION_UNITS = new Set<FuelConsumptionUnit>(['l/h', 'kg/h', 'lb/h', 'imp-gal/h', 'us-gal/h'])

function normalizeResult(value: number | null, decimals?: number): number | null {
  if (value === null) return null
  return nanCorrection(roundToDecimals(value, decimals))
}

function convertWithFactors<T extends string>(
  value: number,
  from: T,
  to: T,
  factors: Record<T, number>,
  decimals?: number
): number {
  if (from === to) return normalizeResult(value, decimals)

  const valueInDomain = value / factors[from]
  const valueInTarget = valueInDomain * factors[to]
  return normalizeResult(valueInTarget, decimals)
}

function validateFuelType(fuelType?: FuelType): FuelType {
  switch (fuelType) {
    case 'avgas':
    case 'jet-a':
    case 'ethanol':
    case 'mogas':
      return fuelType
    default:
      return DEFAULT_FUEL_TYPE
  }
}

export function isWeightUnit(unit: FuelQuantityUnit): unit is WeightUnit {
  return unit === 'kg' || unit === 'lb'
}

function isVolumeUnit(unit: FuelQuantityUnit): unit is VolumeUnit {
  return unit === 'l' || unit === 'imp-gal' || unit === 'us-gal'
}

function isValidWeightUnit(unit: WeightUnit | undefined): unit is WeightUnit {
  return unit !== undefined && VALID_WEIGHT_UNITS.has(unit)
}

function isValidVolumeUnit(unit: VolumeUnit | undefined): unit is VolumeUnit {
  return unit !== undefined && VALID_VOLUME_UNITS.has(unit)
}

function isValidLinearMeasureUnit(unit: LinearMeasureUnit | undefined): unit is LinearMeasureUnit {
  return unit !== undefined && VALID_LINEAR_MEASURE_UNITS.has(unit)
}

function isValidAltitudeUnit(unit: AltitudeUnit | undefined): unit is AltitudeUnit {
  return unit !== undefined && VALID_ALTITUDE_UNITS.has(unit)
}

function isValidSpeedUnit(unit: SpeedUnit | undefined): unit is SpeedUnit {
  return unit !== undefined && VALID_SPEED_UNITS.has(unit)
}

function isValidRateUnit(unit: RateUnit | undefined): unit is RateUnit {
  return unit !== undefined && VALID_RATE_UNITS.has(unit)
}

function isValidFuelQuantityUnit(unit: FuelQuantityUnit | undefined): unit is FuelQuantityUnit {
  return unit !== undefined && VALID_FUEL_QUANTITY_UNITS.has(unit)
}

function isValidFuelConsumptionUnit(unit: FuelConsumptionUnit | undefined): unit is FuelConsumptionUnit {
  return unit !== undefined && VALID_FUEL_CONSUMPTION_UNITS.has(unit)
}

export const getEquivalentFuelConsumptionUnit = (unit: FuelQuantityUnit): FuelConsumptionUnit => {
  switch (unit) {
    case 'l':
      return 'l/h'
    case 'kg':
      return 'kg/h'
    case 'lb':
      return 'lb/h'
    case 'imp-gal':
      return 'imp-gal/h'
    case 'us-gal':
      return 'us-gal/h'
    default:
      return unit
  }
}

export const getEquivalentFuelQuantityUnit = (unit: FuelConsumptionUnit): FuelQuantityUnit => {
  switch (unit) {
    case 'l/h':
      return 'l'
    case 'kg/h':
      return 'kg'
    case 'lb/h':
      return 'lb'
    case 'imp-gal/h':
      return 'imp-gal'
    case 'us-gal/h':
      return 'us-gal'
    default:
      return unit
  }
}

function convertAltitude(params: ScalarConversionParams<AltitudeUnit>): number | null {
  const { value, from = DOMAIN_UNITS.altitude, to = DOMAIN_UNITS.altitude, decimals } = params
  if (isNotSet(value)) return null
  if (!isValidAltitudeUnit(from) || !isValidAltitudeUnit(to)) return null
  return convertWithFactors(value, from, to, FACTORS.altitude, decimals)
}

function convertLinearMeasure(params: ScalarConversionParams<LinearMeasureUnit>): number | null {
  const { value, from = DOMAIN_UNITS.linearMeasure, to = DOMAIN_UNITS.linearMeasure, decimals } = params
  if (isNotSet(value)) return null
  if (!isValidLinearMeasureUnit(from) || !isValidLinearMeasureUnit(to)) return null
  return convertWithFactors(value, from, to, FACTORS.linearMeasure, decimals)
}

function convertRate(params: ScalarConversionParams<RateUnit>): number | null {
  const { value, from = DOMAIN_UNITS.rate, to = DOMAIN_UNITS.rate, decimals } = params
  if (isNotSet(value)) return null
  if (!isValidRateUnit(from) || !isValidRateUnit(to)) return null
  return convertWithFactors(value, from, to, FACTORS.rate, decimals)
}

function convertSpeed(params: ScalarConversionParams<SpeedUnit>): number | null {
  const { value, from = DOMAIN_UNITS.speed, to = DOMAIN_UNITS.speed, decimals } = params
  if (isNotSet(value)) return null
  if (!isValidSpeedUnit(from) || !isValidSpeedUnit(to)) return null
  return convertWithFactors(value, from, to, FACTORS.speed, decimals)
}

function convertVolume(params: ScalarConversionParams<VolumeUnit>): number | null {
  const { value, from = DOMAIN_UNITS.volume, to = DOMAIN_UNITS.volume, decimals } = params
  if (isNotSet(value)) return null
  if (!isValidVolumeUnit(from) || !isValidVolumeUnit(to)) return null
  return convertWithFactors(value, from, to, FACTORS.volume, decimals)
}

function convertWeight(params: ScalarConversionParams<WeightUnit>): number | null {
  const { value, from = DOMAIN_UNITS.weight, to = DOMAIN_UNITS.weight, decimals } = params
  if (isNotSet(value)) return null
  if (!isValidWeightUnit(from) || !isValidWeightUnit(to)) return null
  return convertWithFactors(value, from, to, FACTORS.weight, decimals)
}

function convertFuelQuantity(params: FuelQuantityConversionParams): number | null {
  const { value, from = DOMAIN_UNITS.fuelQuantity, to = DOMAIN_UNITS.fuelQuantity, fuelType, decimals } = params

  if (isNotSet(value)) return null
  if (!isValidFuelQuantityUnit(from) || !isValidFuelQuantityUnit(to)) return null
  if (from === to) return normalizeResult(value, decimals)

  const validatedFuelType = validateFuelType(fuelType)
  const densityKgPerLiter = FUEL_DENSITY_KG_PER_LITER[validatedFuelType]

  if (isWeightUnit(from) && isWeightUnit(to)) {
    return convertWeight({ value, from, to, decimals })
  }

  if (isVolumeUnit(from) && isVolumeUnit(to)) {
    return convertVolume({ value, from, to, decimals })
  }

  if (isVolumeUnit(from) && isWeightUnit(to)) {
    const liters = convertVolume({ value, from, to: 'l' })
    if (liters === null) return null
    const weightInKg = liters * densityKgPerLiter
    return convertWeight({ value: weightInKg, from: 'kg', to, decimals })
  }

  if (isWeightUnit(from) && isVolumeUnit(to)) {
    const weightInKg = convertWeight({ value, from, to: 'kg' })
    if (weightInKg === null) return null
    const liters = weightInKg / densityKgPerLiter
    return convertVolume({ value: liters, from: 'l', to, decimals })
  }

  return null
}

function convertFuelConsumption(params: FuelConsumptionConversionParams): number | null {
  const { value, from = DOMAIN_UNITS.fuelConsumption, to = DOMAIN_UNITS.fuelConsumption, fuelType, decimals } = params

  if (isNotSet(value)) return null
  if (!isValidFuelConsumptionUnit(from) || !isValidFuelConsumptionUnit(to)) return null
  if (from === to) return normalizeResult(value, decimals)

  const fromQuantityUnit = getEquivalentFuelQuantityUnit(from)
  const toQuantityUnit = getEquivalentFuelQuantityUnit(to)

  return convertFuelQuantity({
    value,
    from: fromQuantityUnit,
    to: toQuantityUnit,
    fuelType,
    decimals
  })
}

function convertFuelQuantityToWeight(params: FuelQuantityToWeightParams): number | null {
  const { value, from, to, fuelType, decimals } = params
  if (isNotSet(value)) return null
  if (!isValidFuelQuantityUnit(from) || !isValidWeightUnit(to)) return null

  return convertFuelQuantity({
    value,
    from,
    to,
    fuelType,
    decimals
  })
}

function convertWeightToFuelQuantity(params: WeightToFuelQuantityParams): number | null {
  const { value, from, to, fuelType, decimals } = params
  if (isNotSet(value)) return null
  if (!isValidWeightUnit(from) || !isValidFuelQuantityUnit(to)) return null

  return convertFuelQuantity({
    value,
    from,
    to,
    fuelType,
    decimals
  })
}

function displayWeight(unit: WeightUnit): string {
  return DISPLAY_MAPS.weight[unit] ?? DISPLAY_MAPS.weight[DOMAIN_UNITS.weight]
}

function displayVolume(unit: VolumeUnit): string {
  return DISPLAY_MAPS.volume[unit] ?? DISPLAY_MAPS.volume[DOMAIN_UNITS.volume]
}

function displayLinearMeasure(unit: LinearMeasureUnit): string {
  return DISPLAY_MAPS.linearMeasure[unit] ?? DISPLAY_MAPS.linearMeasure[DOMAIN_UNITS.linearMeasure]
}

function displayAltitude(unit: AltitudeUnit): string {
  return DISPLAY_MAPS.altitude[unit] ?? DISPLAY_MAPS.altitude[DOMAIN_UNITS.altitude]
}

function displayFuelQuantity(unit: FuelQuantityUnit, unifyGallonUnits?: boolean): string {
  if (unifyGallonUnits && (unit === 'us-gal' || unit === 'imp-gal')) return 'Gal'
  return DISPLAY_MAPS.fuelQuantity[unit] ?? DISPLAY_MAPS.fuelQuantity[DOMAIN_UNITS.fuelQuantity]
}

function displayFuelConsumption(unit: FuelConsumptionUnit, unifyGallonUnits?: boolean): string {
  if (unifyGallonUnits && (unit === 'us-gal/h' || unit === 'imp-gal/h')) return 'Gal/h'
  return DISPLAY_MAPS.fuelConsumption[unit] ?? DISPLAY_MAPS.fuelConsumption[DOMAIN_UNITS.fuelConsumption]
}

/**
 * As unidades chegam do backend como texto livre: o catálogo de modelos já enviou variantes com
 * espaço em vez de hífen ("us gal/h"), e envia string vazia nos modelos sem performance. Qualquer
 * valor fora do enum faz os conversores acima devolverem `null`, apagando silenciosamente o dado
 * convertido — por isso normalizamos na borda de entrada (repositórios).
 *
 * A canonicalização cobre caixa, espaços em volta da barra e espaço/underscore como separador,
 * o que também aceita os rótulos de exibição ("US Gal/h", "IMP Gal/h", "L/h").
 */
function canonicalizeUnitText(rawUnit: unknown): string {
  if (typeof rawUnit !== 'string') return ''

  return rawUnit
    .trim()
    .toLowerCase()
    .replace(/\s*\/\s*/g, '/') // "us gal / h" -> "us gal/h"
    .replace(/[\s_]+/g, '-') // "us gal/h" -> "us-gal/h"
}

function matchUnit<T extends string>(rawUnit: unknown, validUnits: Set<T>): T | null {
  const canonicalUnit = canonicalizeUnitText(rawUnit) as T

  return validUnits.has(canonicalUnit) ? canonicalUnit : null
}

/**
 * Devolve a unidade canônica equivalente ou `null` quando não reconhece o valor. Cabe a quem chama
 * decidir o fallback, e ele **não é o mesmo em todo lugar**:
 *
 * - Onde o número gravado está em unidade de domínio e a coluna de unidade é só rótulo de exibição
 *   (aeronave do piloto, catálogo de modelos), o fallback é a unidade de domínio: exibe o valor como
 *   está gravado, sem conversão.
 * - Onde o número está **na unidade que a própria linha declara** (perfil de peso e balanceamento),
 *   recuar para o domínio reinterpretaria o valor — 2,2× em peso, 25,4× em braço. Ali o rótulo cru
 *   fica como está e a conversão falha alto mais adiante, que é o comportamento honesto.
 */
export const normalizeUnit = {
  altitude: (rawUnit: unknown): AltitudeUnit | null => matchUnit(rawUnit, VALID_ALTITUDE_UNITS),
  fuelConsumption: (rawUnit: unknown): FuelConsumptionUnit | null => matchUnit(rawUnit, VALID_FUEL_CONSUMPTION_UNITS),
  fuelQuantity: (rawUnit: unknown): FuelQuantityUnit | null => matchUnit(rawUnit, VALID_FUEL_QUANTITY_UNITS),
  linearMeasure: (rawUnit: unknown): LinearMeasureUnit | null => matchUnit(rawUnit, VALID_LINEAR_MEASURE_UNITS),
  rate: (rawUnit: unknown): RateUnit | null => matchUnit(rawUnit, VALID_RATE_UNITS),
  speed: (rawUnit: unknown): SpeedUnit | null => matchUnit(rawUnit, VALID_SPEED_UNITS),
  volume: (rawUnit: unknown): VolumeUnit | null => matchUnit(rawUnit, VALID_VOLUME_UNITS),
  weight: (rawUnit: unknown): WeightUnit | null => matchUnit(rawUnit, VALID_WEIGHT_UNITS)
} as const

export const convertUnit = {
  altitude: convertAltitude,
  fuelConsumption: convertFuelConsumption,
  fuelQuantity: convertFuelQuantity,
  fuelQuantityToWeight: convertFuelQuantityToWeight,
  linearMeasure: convertLinearMeasure,
  rate: convertRate,
  speed: convertSpeed,
  volume: convertVolume,
  weight: convertWeight,
  weightToFuelQuantity: convertWeightToFuelQuantity
} as const

export const unitConverters = {
  altitude(value: number, unit: AltitudeUnit, toDomain: boolean = false, decimalPlaces?: number): number | null {
    return toDomain
      ? convertUnit.altitude({ value, from: unit, to: DOMAIN_UNITS.altitude, decimals: decimalPlaces })
      : convertUnit.altitude({ value, from: DOMAIN_UNITS.altitude, to: unit, decimals: decimalPlaces })
  },
  fuel(
    value: number,
    fuelType: FuelType,
    unit: FuelConsumptionUnit,
    toDomain: boolean = false,
    decimalPlaces?: number
  ) {
    return toDomain
      ? convertUnit.fuelConsumption({
          value,
          from: unit,
          to: DOMAIN_UNITS.fuelConsumption,
          fuelType,
          decimals: decimalPlaces
        })
      : convertUnit.fuelConsumption({
          value,
          from: DOMAIN_UNITS.fuelConsumption,
          to: unit,
          fuelType,
          decimals: decimalPlaces
        })
  },
  fuelQuantity(
    value: number,
    fuelType: FuelType,
    unit: FuelQuantityUnit,
    toDomain: boolean = false,
    decimalPlaces?: number
  ) {
    return toDomain
      ? convertUnit.fuelQuantity({
          value,
          from: unit,
          to: DOMAIN_UNITS.fuelQuantity,
          fuelType,
          decimals: decimalPlaces
        })
      : convertUnit.fuelQuantity({
          value,
          from: DOMAIN_UNITS.fuelQuantity,
          to: unit,
          fuelType,
          decimals: decimalPlaces
        })
  },
  fuelQuantityToWeight(
    value: number,
    fuelType: FuelType,
    fromUnit: FuelQuantityUnit,
    toUnit: WeightUnit,
    decimalPlaces?: number
  ) {
    return convertUnit.fuelQuantityToWeight({
      value,
      from: fromUnit,
      to: toUnit,
      fuelType,
      decimals: decimalPlaces
    })
  },
  linearMeasure(value: number, unit: LinearMeasureUnit, toDomain: boolean = false, decimalPlaces?: number) {
    return toDomain
      ? convertUnit.linearMeasure({
          value,
          from: unit,
          to: DOMAIN_UNITS.linearMeasure,
          decimals: decimalPlaces
        })
      : convertUnit.linearMeasure({
          value,
          from: DOMAIN_UNITS.linearMeasure,
          to: unit,
          decimals: decimalPlaces
        })
  },
  rate(value: number, unit: RateUnit, toDomain: boolean = false, decimalPlaces?: number) {
    return toDomain
      ? convertUnit.rate({ value, from: unit, to: DOMAIN_UNITS.rate, decimals: decimalPlaces })
      : convertUnit.rate({ value, from: DOMAIN_UNITS.rate, to: unit, decimals: decimalPlaces })
  },
  speed(value: number, unit: SpeedUnit, toDomain: boolean = false, decimalPlaces?: number) {
    return toDomain
      ? convertUnit.speed({ value, from: unit, to: DOMAIN_UNITS.speed, decimals: decimalPlaces })
      : convertUnit.speed({ value, from: DOMAIN_UNITS.speed, to: unit, decimals: decimalPlaces })
  },
  volume(value: number, unit: VolumeUnit, toDomain: boolean = false, decimalPlaces?: number) {
    return toDomain
      ? convertUnit.volume({ value, from: unit, to: DOMAIN_UNITS.volume, decimals: decimalPlaces })
      : convertUnit.volume({ value, from: DOMAIN_UNITS.volume, to: unit, decimals: decimalPlaces })
  },
  weight(value: number, unit: WeightUnit, toDomain: boolean = false, decimalPlaces?: number) {
    return toDomain
      ? convertUnit.weight({ value, from: unit, to: DOMAIN_UNITS.weight, decimals: decimalPlaces })
      : convertUnit.weight({ value, from: DOMAIN_UNITS.weight, to: unit, decimals: decimalPlaces })
  },
  weightToFuelQuantity(
    value: number,
    fuelType: FuelType,
    fromUnit: WeightUnit,
    toUnit: FuelQuantityUnit,
    decimalPlaces?: number
  ) {
    return convertUnit.weightToFuelQuantity({
      value,
      from: fromUnit,
      to: toUnit,
      fuelType,
      decimals: decimalPlaces
    })
  }
} as const

export const displayUnit = {
  altitude: displayAltitude,
  any: (unit: Unit): string => {
    if (unit in DISPLAY_MAPS.weight) return displayWeight(unit as WeightUnit)
    if (unit in DISPLAY_MAPS.volume) return displayVolume(unit as VolumeUnit)
    if (unit in DISPLAY_MAPS.linearMeasure) return displayLinearMeasure(unit as LinearMeasureUnit)
    if (unit in DISPLAY_MAPS.fuelQuantity) return displayFuelQuantity(unit as FuelQuantityUnit)
    if (unit in DISPLAY_MAPS.fuelConsumption) return displayFuelConsumption(unit as FuelConsumptionUnit)
    if (unit in DISPLAY_MAPS.altitude) return displayAltitude(unit as AltitudeUnit)
    return unit
  },
  fuelConsumption: displayFuelConsumption,
  fuelQuantity: displayFuelQuantity,
  linearMeasure: displayLinearMeasure,
  volume: displayVolume,
  weight: displayWeight
} as const

export const unitDomains = DOMAIN_UNITS
export const fuelDensityKgPerLiter = FUEL_DENSITY_KG_PER_LITER
